import React from 'react';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import Record from 'ui/Record/Record';

import Grid from 'components/Grid';
import { MainHeader } from 'components/MainHeader/MainHeader';
import { VatixError } from 'components/Error/types';

import routes from 'core/routes';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { UserFieldGridRow } from 'utils/api/modules/user/user.types';

import { basicColumns } from './components/columns';
import UserFieldEditModal from './components/UserFieldEditModal';

const getRowId = (row: UserFieldGridRow): string =>
  row.kind === 'dropdown' ? `dropdown-${row.key}` : `custom-${row.slot}`;

const UserFieldsGrid: React.FunctionComponent<{ onError: (error: VatixError) => void }> = ({ onError }) => {
  const refreshRef = React.useRef<(() => void) | null>(null);
  const [activeRow, setActiveRow] = React.useState<UserFieldGridRow | null>(null);

  const onRowClick: GridEventListener<'rowClick'> = ({ row }: GridRowParams): void => {
    setActiveRow(row as UserFieldGridRow);
  };

  return (
    <>
      <Grid
        key="user_fields_grid"
        entity="user fields"
        basicColumns={basicColumns}
        dataURL="user-fields/custom-fields/"
        transformData={(response) => {
          const rows = response as UserFieldGridRow[];
          return { count: rows.length, results: rows };
        }}
        getRowId={(row) => getRowId(row as UserFieldGridRow)}
        onError={onError}
        onRowClick={onRowClick}
        onRefreshCallback={(callback) => {
          refreshRef.current = callback;
        }}
        customNoRowsText="No user fields to show"
        disableColumnSelector
        disableColumnFilter
        disableExport
        disableSavePreferences
      />
      {activeRow !== null && (
        <UserFieldEditModal
          row={activeRow}
          onClose={(refresh) => {
            setActiveRow(null);
            if (refresh) {
              refreshRef.current?.();
            }
          }}
        />
      )}
    </>
  );
};

const UserFieldsSettings: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();

  return (
    <Record
      key="user_fields_settings"
      Header={
        <MainHeader
          title="User Fields"
          breadcrumbs={[
            { label: 'Organisation', link: '' },
            { label: 'User Fields', link: routes.dashboard.settings.userFields.toString() },
          ]}
        />
      }
      Content={(onError) => <UserFieldsGrid onError={onError} />}
      navBarHeight={navBarHeight}
    />
  );
};

export default UserFieldsSettings;
